import React, {
  FunctionComponent,
  ReactNode,
  useEffect,
  useState
} from "react";

import {
  Checkbox,
  FormControl,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectProps
} from "@material-ui/core";
import { StorePickupBox, StorePickupBoxId } from "../../models/Store";
import * as StoreApi from "../../api/storeApi";
import { isOk } from "../../result";

interface StorePickupBoxMultiSelectProps extends Omit<SelectProps, "loading"> {
  readonly storeId: string;
  readonly label?: string;
  readonly onSelected?: (value: ReadonlyArray<StorePickupBox>) => void;
  readonly variant?: "filled" | "outlined" | "standard";
  readonly value?: ReadonlyArray<StorePickupBoxId>;
}

export const StorePickupBoxMultiSelect: FunctionComponent<StorePickupBoxMultiSelectProps> = ({
  storeId,
  label = "Pickup box(es)",
  onSelected,
  onChange,
  variant = "standard",
  value = [],
  ...props
}) => {
  const [state, setState] = useState<ReadonlyArray<StorePickupBox>>([]);

  const handleChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
    child: React.ReactNode
  ) => {
    if (onSelected) {
      let ids = event.target.value as Array<StorePickupBoxId>;
      let list = state.filter(cluster => ids.includes(cluster.id));

      onSelected(list ?? []);
    }
    if (onChange) {
      onChange(event, child);
    }
  };

  const renderName = (item: StorePickupBox): string => {
    return `Slot ${item.slot}`;
  };

  const renderNames = (ids: ReadonlyArray<StorePickupBoxId>): ReactNode => {
    return state
      .filter(item => ids.includes(item.id))
      .map(item => renderName(item))
      .join(", ");
  };

  useEffect(() => {
    // load if not loaded
    if (!state || !state.length) {
      StoreApi.listEmptyPickupBoxes(storeId).then(result => {
        if (isOk(result)) {
          return setState(result.value);
        }

        console.error(result.details);
        return setState([]);
      });
    }
  }, []);

  return (
    <FormControl variant={variant} className={props.className}>
      <InputLabel
        id="boxes-multi-select-label"
        style={
          variant === "outlined"
            ? {
                backgroundColor: "#fff",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem"
              }
            : undefined
        }
      >
        {label}
      </InputLabel>
      <Select
        multiple
        value={value}
        onChange={handleChange}
        labelId="boxes-multi-select-label"
        id="boxes-multi-select"
        autoWidth={true}
        disabled={props.disabled}
        input={<Input />}
        renderValue={value => renderNames(value as string[])}
      >
        {state &&
          state.length > 0 &&
          state.map(item => (
            <MenuItem key={item.id} value={item.id}>
              <Checkbox checked={value.indexOf(item.id) > -1} />
              <ListItemText primary={renderName(item)} />
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
};
